import ProgressBar from "@ramonak/react-progress-bar";
import { motion } from "framer-motion";
import { ceil } from "lodash";
import { FaArrowUp, FaBolt, FaEye, FaShieldAlt } from "react-icons/fa";
import styled from "styled-components";
import { cutoutUrlFor } from '../../data/characterCutouts';

/* ─────────────── Team panel — mass-poster design system ───────────────
   Structure: vertical stack of character tiles inside a scrollable column.
   Each tile is an ink card with a cut-corner, photo fills it edge-to-edge,
   HP bar lives across the bottom, active card gets an amber border + glow.
   Opponent unrevealed tiles = wine panel with oversized '?' (poster feel).
----------------------------------------------------------------------- */

const Panel = styled.div`
  padding: var(--space-2);
  height: 100%;
  max-height: 100%;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  scrollbar-width: thin;
  scrollbar-color: var(--color-ink-500) transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--color-ink-500);
    border-radius: 2px;
  }
`;

const PanelHeader = styled.div`
  text-align: center;
  flex-shrink: 0;
  margin-bottom: var(--space-2);
  padding-bottom: var(--space-2);
  border-bottom: 1px solid var(--color-ink-700);

  span {
    font-family: var(--font-heading);
    font-size: var(--size-2xs);
    font-weight: var(--weight-bold);
    letter-spacing: var(--tracking-widest);
    text-transform: uppercase;
    color: ${(p) => (p.$isUser ? 'var(--color-amber-500)' : 'var(--color-red-500)')};
  }
`;

const RosterList = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: var(--space-2);
`;

const TeamCardWrapper = styled(motion.div)`
  width: 100%;
  transition: transform var(--dur-fast) var(--ease-out);

  ${(p) =>
        p.$isUser &&
        `&:hover { transform: translateY(-1px); }`}
`;

/* Replaces react-bootstrap Card. Cut corner, ink body, accent ring when active. */
const CharacterCard = styled.button`
  all: unset;
  box-sizing: border-box;
  display: block;
  position: relative;
  width: 100%;
  height: 92px;
  background: var(--color-ink-800);
  color: var(--color-cream-100);
  cursor: ${(p) => (p.$isUser ? 'pointer' : 'default')};
  overflow: hidden;
  clip-path: var(--clip-button);
  box-shadow: var(--shadow-sm);
  transition: box-shadow var(--dur-fast) var(--ease-out),
              outline-color var(--dur-fast) var(--ease-out);

  /* Active character — amber accent across the top + glow */
  ${(p) =>
        p.$active &&
        `
    box-shadow: var(--shadow-glow-amber);
    &::before {
      content: '';
      position: absolute;
      left: 0; right: 0; top: 0;
      height: 3px;
      background: var(--color-amber-500);
      z-index: 3;
    }
  `}

  /* Defeated state — red bar across the top, reduced opacity */
  ${(p) =>
        p.$defeated &&
        `
    &::before {
      content: '';
      position: absolute;
      left: 0; right: 0; top: 0;
      height: 3px;
      background: var(--color-red-500);
      z-index: 3;
    }
  `}

  &:disabled {
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid var(--color-amber-500);
    outline-offset: 2px;
  }
`;

const PortraitWrap = styled.div`
  position: absolute;
  inset: 0 0 26px 0;
  overflow: hidden;
  /* Subtle red gradient so transparent cutouts still read as a poster
     card, not a floating head. */
  background:
    radial-gradient(ellipse at 50% 40%, rgba(193, 39, 45, 0.35) 0%, transparent 70%),
    linear-gradient(180deg, var(--color-red-900) 0%, var(--color-ink-800) 100%);
`;

/* Upper-half crop. For the new transparent-background cutouts (full-body
   3:4) we scale up with `object-fit: cover` and anchor `object-position`
   to the top so head + shoulders fill the tile — server CDN face-crops
   still look fine under the same rule since they're already head-biased. */
const Portrait = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
  /* Zoom the cutout ~1.4× from the top so the face sits in the vertical
     center of the tile instead of being flush with the top edge. */
  ${(p) => p.$isCutout && `
    transform: scale(1.4);
    transform-origin: center top;
  `}
  ${(p) => p.$defeated && `filter: grayscale(100%) brightness(0.55);`}
`;

const HiddenPortrait = styled.div`
  position: absolute;
  inset: 0 0 26px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(circle at center, var(--color-red-700) 0%, var(--color-red-900) 80%);
  font-family: var(--font-display);
  font-size: 42px;
  font-weight: var(--weight-bold);
  color: rgba(8, 7, 10, 0.35);
  letter-spacing: 0;
  line-height: 1;

  /* Halftone feel on the hidden card */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      radial-gradient(circle, rgba(8, 7, 10, 0.35) 1px, transparent 1px);
    background-size: 6px 6px;
    opacity: 0.25;
    pointer-events: none;
  }
`;

const HpBadge = styled.div`
  position: absolute;
  top: 6px;
  right: 6px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 5px;
  background: rgba(8, 7, 10, 0.78);
  border-left: 2px solid ${(p) => p.$color};
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: var(--weight-bold);
  color: var(--color-cream-100);
  line-height: 1;
`;

const Footer = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 3px 6px 5px;
  background: var(--color-ink-950);
  z-index: 2;
`;

const Name = styled.div`
  font-family: var(--font-heading);
  font-size: 9px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-wide);
  text-transform: uppercase;
  color: var(--color-cream-100);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;
`;

const StatBadgeStack = styled.div`
  position: absolute;
  bottom: 32px;
  right: 4px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  z-index: 3;
`;

const StatBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 1px 4px;
  font-family: var(--font-mono);
  font-size: 7px;
  font-weight: var(--weight-bold);
  letter-spacing: 0.02em;
  color: var(--color-cream-100);
  background: ${(p) =>
        p.$increase ? 'var(--color-success)' : 'var(--color-danger)'};
  opacity: 0.95;
`;

const TeamLayout = (props) => {
    const isUser = props.type === 'user';

    const CharacterChoiceCard = (charecter, index) => {
        const isActive = props.selectedCharacter && props.selectedCharacter._id === charecter._id;
        const hidden = !isUser && charecter.isRevealed !== true;
        const defeated = charecter.modifiedHealth <= 0;

        const onChangeCharecter = () => {
            if (isUser) props.onClickCharecter(charecter);
        };

        const getHealthPercentage = () => {
            let percentage = 100;
            if (charecter.hasOwnProperty('modifiedHealth')) {
                percentage = charecter.modifiedHealth > 0
                    ? ceil((charecter.modifiedHealth / charecter.health) * 100)
                    : 0;
            }
            return percentage;
        };

        const getStatChanges = () => {
            const changes = [];
            const statMap = [
                { key: 'Attack', stat: 'ATK', icon: <FaArrowUp size={5} /> },
                { key: 'Defense', stat: 'DEF', icon: <FaShieldAlt size={5} /> },
                { key: 'Speed', stat: 'SPD', icon: <FaBolt size={5} /> },
                { key: 'Accuracy', stat: 'ACC', icon: <FaEye size={5} /> },
            ];

            statMap.forEach(({ key, stat, icon }) => {
                const originalKey = `original${key}`;
                const currentKey = key.toLowerCase();
                if (charecter[originalKey] !== undefined &&
                    charecter[currentKey] !== charecter[originalKey]) {
                    const original = charecter[originalKey];
                    const current = charecter[currentKey];
                    changes.push({
                        stat,
                        icon,
                        increase: current > original,
                        percentage: Math.abs(((current - original) / original) * 100).toFixed(0),
                    });
                }
            });
            return changes;
        };

        const healthPercentage = getHealthPercentage();
        const hpColor =
            healthPercentage > 60 ? 'var(--color-success)'
                : healthPercentage > 30 ? 'var(--color-amber-500)'
                    : 'var(--color-red-500)';

        const cardVariants = {
            hidden: { opacity: 0, x: isUser ? -12 : 12 },
            visible: (i) => ({
                opacity: 1,
                x: 0,
                transition: { delay: i * 0.08, duration: 0.35 },
            }),
        };

        const statChanges = !hidden && charecter.modifiedHealth > 0 ? getStatChanges() : [];

        /* Prefer the local mass-poster cutout; fall back to the server CDN
           thumbnail when a character isn't mapped in `characterCutouts.js`.
           The `isCutout` flag toggles the upper-body zoom in <Portrait>. */
        const resolvedCutout = cutoutUrlFor(charecter);
        const portraitSrc = resolvedCutout || charecter.image;
        const isCutout = Boolean(resolvedCutout);

        return (
            <TeamCardWrapper
                $isUser={isUser}
                custom={index}
                initial="hidden"
                animate="visible"
                variants={cardVariants}
            >
                <CharacterCard
                    $active={isActive}
                    $isUser={isUser}
                    $defeated={defeated}
                    onClick={onChangeCharecter}
                    className={`team-character-button character-card ${isActive ? 'active-character' : ''}`}
                    data-defeated={defeated ? 'true' : 'false'}
                    data-active={isActive ? 'true' : 'false'}
                    data-character-id={charecter._id}
                    data-character-name={charecter.name}
                    type="button"
                >
                    {hidden ? (
                        <>
                            <HiddenPortrait>?</HiddenPortrait>
                            <Footer>
                                <Name style={{ color: 'var(--color-cream-300)' }}>
                                    Unrevealed
                                </Name>
                            </Footer>
                        </>
                    ) : (
                        <>
                            <PortraitWrap>
                                <Portrait
                                    src={portraitSrc}
                                    alt={charecter.name}
                                    $defeated={defeated}
                                    $isCutout={isCutout}
                                />
                            </PortraitWrap>

                            <HpBadge $color={hpColor}>
                                {healthPercentage}%
                            </HpBadge>

                            {statChanges.length > 0 && (
                                <StatBadgeStack>
                                    {statChanges.map((change, idx) => (
                                        <StatBadge
                                            key={idx}
                                            $increase={change.increase}
                                            title={`${change.stat} ${change.increase ? '+' : '-'}${change.percentage}%`}
                                        >
                                            {change.icon}
                                            {change.stat}{change.increase ? '+' : '-'}{change.percentage}%
                                        </StatBadge>
                                    ))}
                                </StatBadgeStack>
                            )}

                            <Footer>
                                <Name>{charecter.name}</Name>
                                <ProgressBar
                                    isLabelVisible={false}
                                    completed={healthPercentage}
                                    bgColor={
                                        healthPercentage > 60 ? '#67C97A'
                                            : healthPercentage > 30 ? '#FFB13C'
                                                : '#C1272D'
                                    }
                                    height="3px"
                                    baseBgColor="rgba(8, 7, 10, 0.6)"
                                    borderRadius="0"
                                />
                            </Footer>
                        </>
                    )}
                </CharacterCard>
            </TeamCardWrapper>
        );
    };

    return (
        <Panel className="team-layout-container">
            <PanelHeader $isUser={isUser}>
                <span>{isUser ? 'Your Team' : 'Opponent'}</span>
            </PanelHeader>

            <RosterList>
                {props.team && props.team.map((charecter, index) => (
                    <div key={charecter._id || index}>
                        {CharacterChoiceCard(charecter, index)}
                    </div>
                ))}
            </RosterList>
        </Panel>
    );
};

export default TeamLayout;
